import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { registerApi, verifyOtpApi } from '../api/auth';
import API from '../api/axios';
import { useAuthStore } from '../store/authStore';
import { RegisterPayload } from '../types';

interface Branch {
  id: number;
  name: string;
  address: string;
}

const RegisterPage: React.FC = () => {
  const [step, setStep] = useState<'register' | 'verify'>('register');
  const [loading, setLoading] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [registeredData, setRegisteredData] = useState<any>(null);
  const [branches, setBranches] = useState<Branch[]>([]);
  const { setAuth } = useAuthStore();

  const { register, handleSubmit } = useForm<RegisterPayload & { preferred_branch_id?: string }>();

  useEffect(() => {
    API.get('/auth/branches').then(res => {
      setBranches(res.data.branches || []);
    }).catch(() => {});
  }, []);

  const onRegister = async (data: any) => {
    if (!data.email && !data.phone) {
      toast.error('Please provide email or phone number');
      return;
    }
    if (data.password !== data.password_confirmation) {
      toast.error('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      await registerApi({
        ...data,
        preferred_branch_id: data.preferred_branch_id || undefined,
      });
      setRegisteredData(data);
      setStep('verify');
      toast.success('Account created! Please verify your account.');
    } catch (err: any) {
      const errors = err.response?.data?.errors;
      if (errors) toast.error(errors[0]);
      else toast.error(err.response?.data?.error || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const onVerify = async () => {
    if (otpCode.length !== 6) { toast.error('Please enter the 6-digit code'); return; }
    setLoading(true);
    try {
      const otpType = registeredData?.phone ? 'phone_verification' : 'email_verification';
      const res = await verifyOtpApi({
        code: otpCode,
        otp_type: otpType,
        phone: registeredData?.phone,
        email: registeredData?.email,
      });
      setAuth(res.user, res.access_token, res.refresh_token);
      toast.success('Welcome to Honeysuckle Rewards!');
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '80px 20px',
      position: 'relative',
      zIndex: 1,
    }}>
      <div style={{
        position: 'fixed', top: '40%', left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '500px', height: '500px',
        background: 'radial-gradient(circle, rgba(255,255,255,0.03) 0%, transparent 70%)',
        pointerEvents: 'none',
      }} />

      <div style={{ width: '100%', maxWidth: '460px', animation: 'fadeUp 0.5s ease forwards' }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <Link to="/" style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', marginBottom: '32px' }}>
            <div style={{ width: '32px', height: '32px', background: '#fff', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ color: '#000', fontSize: '16px', fontWeight: '800' }}>H</span>
            </div>
            <span style={{ fontSize: '16px', fontWeight: '600' }}>Honeysuckle</span>
          </Link>
          <h1 style={{ fontSize: '28px', fontWeight: '700', letterSpacing: '-0.03em', marginBottom: '8px' }}>
            {step === 'register' ? 'Create your account' : 'Verify your account'}
          </h1>
          <p style={{ fontSize: '14px', color: 'var(--gray-400)' }}>
            {step === 'register' ? 'Join free and start earning rewards today' : `Enter the code sent to your ${registeredData?.phone ? 'phone' : 'email'}`}
          </p>
        </div>

        {/* Progress */}
        <div style={{ display: 'flex', gap: '6px', marginBottom: '32px' }}>
          {['Account Details', 'Verify'].map((s, i) => (
            <div key={s} style={{ flex: 1 }}>
              <div style={{
                height: '3px', borderRadius: '2px',
                background: i === 0 || step === 'verify' ? '#fff' : 'rgba(255,255,255,0.15)',
                transition: 'background 0.3s',
              }} />
              <span style={{ fontSize: '11px', color: i === 0 || step === 'verify' ? 'var(--gray-300)' : 'var(--gray-600)', marginTop: '6px', display: 'block' }}>{s}</span>
            </div>
          ))}
        </div>

        {/* Card */}
        <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border)', borderRadius: '16px', padding: '32px', backdropFilter: 'blur(20px)' }}>
          {step === 'register' ? (
            <form onSubmit={handleSubmit(onRegister)}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>

                {/* Name */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <label style={{ fontSize: '12px', color: 'var(--gray-400)', marginBottom: '6px', display: 'block' }}>First name</label>
                    <input className="input" placeholder="John" {...register('first_name', { required: true })} />
                  </div>
                  <div>
                    <label style={{ fontSize: '12px', color: 'var(--gray-400)', marginBottom: '6px', display: 'block' }}>Last name</label>
                    <input className="input" placeholder="Doe" {...register('last_name', { required: true })} />
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label style={{ fontSize: '12px', color: 'var(--gray-400)', marginBottom: '6px', display: 'block' }}>Email address</label>
                  <input className="input" type="email" placeholder="you@example.com" {...register('email')} />
                </div>

                {/* Phone */}
                <div>
                  <label style={{ fontSize: '12px', color: 'var(--gray-400)', marginBottom: '6px', display: 'block' }}>Phone number</label>
                  <input className="input" type="tel" placeholder="+233..." {...register('phone')} />
                  <span style={{ fontSize: '11px', color: 'var(--gray-600)', marginTop: '4px', display: 'block' }}>Provide email or phone (or both)</span>
                </div>

                {/* Preferred Branch */}
                <div>
                  <label style={{ fontSize: '12px', color: 'var(--gray-400)', marginBottom: '6px', display: 'block' }}>
                    Preferred branch <span style={{ color: 'var(--gray-600)' }}></span>
                  </label>
                  <select className="input" {...register('preferred_branch_id')}
                    style={{ background: 'rgba(255,255,255,0.04)', color: '#fff' }}>
                    <option value="" style={{ background: '#111' }}>Select a branch...</option>
                    {branches.map(b => (
                      <option key={b.id} value={b.id} style={{ background: '#111' }}>{b.name}</option>
                    ))}
                  </select>
                  <div style={{
                    marginTop: '8px', padding: '10px 12px',
                    background: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(255,255,255,0.06)',
                    borderRadius: '8px', fontSize: '12px',
                    color: 'var(--gray-500)', lineHeight: '1.5',
                  }}>
                    📍 Choose the Honeysuckle location closest to you. When you visit, simply show your digital card and staff will apply your rewards instantly.
                  </div>
                </div>

                {/* Password */}
                <div>
                  <label style={{ fontSize: '12px', color: 'var(--gray-400)', marginBottom: '6px', display: 'block' }}>Password</label>
                  <input className="input" type="password" placeholder="Min. 8 characters" {...register('password', { required: true, minLength: 8 })} />
                </div>

                {/* Confirm Password */}
                <div>
                  <label style={{ fontSize: '12px', color: 'var(--gray-400)', marginBottom: '6px', display: 'block' }}>Confirm password</label>
                  <input className="input" type="password" placeholder="••••••••" {...register('password_confirmation', { required: true })} />
                </div>

                <button type="submit" className="btn-primary"
                  disabled={loading}
                  style={{ width: '100%', justifyContent: 'center', padding: '12px', marginTop: '8px', opacity: loading ? 0.6 : 1, fontSize: '14px' }}>
                  {loading ? 'Creating account...' : 'Create Account →'}
                </button>
              </div>
            </form>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div>
                <label style={{ fontSize: '12px', color: 'var(--gray-400)', marginBottom: '6px', display: 'block' }}>Verification code</label>
                <input className="input" placeholder="000000" maxLength={6}
                  value={otpCode} onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                  style={{ letterSpacing: '0.3em', textAlign: 'center', fontSize: '24px', padding: '16px' }} />
              </div>
              <button onClick={onVerify} className="btn-primary"
                disabled={loading || otpCode.length !== 6}
                style={{ width: '100%', justifyContent: 'center', padding: '12px', opacity: loading || otpCode.length !== 6 ? 0.5 : 1, fontSize: '14px' }}>
                {loading ? 'Verifying...' : 'Verify & Enter →'}
              </button>
              <button onClick={() => setStep('register')} style={{ background: 'none', border: 'none', color: 'var(--gray-500)', fontSize: '13px' }}>
                ← Back to registration
              </button>
            </div>
          )}
        </div>

        <p style={{ textAlign: 'center', marginTop: '24px', fontSize: '13px', color: 'var(--gray-500)' }}>
          Already have an account?{' '}
          <Link to="/login" style={{ color: '#fff', fontWeight: '500' }}>Sign in →</Link>
        </p>
      </div>
    </div>
  );
};

export default RegisterPage;